import {
  useCallback,
  useMemo,
  useState
} from 'react';
import PubSub from 'pubsub-js';

import { useModalContext } from '../providers';

import errorHandler from '../utils/errorHandler';
import Request from '../utils/Request';

/**
 * @description setup a request callback,
 *  you need to use the requestInstance to abort the request on useEffect.
 * @param logic {function} - The function callback to be executed on each request.
 * @param onFailure {function=} - The function callback to be executed on failure.
 * @returns {{
 *  requestInstance: Request,
 *  callback: ((function(...[*]): Promise<*|void|undefined>)|*),
 *  loading: boolean
 * }};
 */
export function useRequestCallback(logic, onFailure, initialLoading = false) {
  const [loading, setLoading] = useState(initialLoading);
  const { openModal, } = useModalContext();

  const requestInstance = useMemo(() => new Request(), []);

  return {
    callback: useCallback(async (...args) => {
      try {
        requestInstance.prepare();
        setLoading(true);
        const response = await logic(requestInstance, ...args);
        return response;
      } catch (error) {
        console.error('error:::api : ', error);
        setLoading(false);
        if (error.name.startsWith('AuthError') && import.meta.env.NX_PUB_SUB_TOPIC_SIGN_OUT) {
          return PubSub.publish(import.meta.env.NX_PUB_SUB_TOPIC_SIGN_OUT);
        }
        if (error.name.startsWith('AbortError') || error.name.startsWith('CanceledError')) {
          return {};
        }
        if (error.name.startsWith('ApiError')) {
          // TODO: display error modal.
        }
        if (onFailure) {
          return onFailure(error, ...args);
        }
        errorHandler(error, openModal);
        throw error;
      } finally {
        setLoading(false);
      }
    }, [logic, onFailure, openModal, requestInstance]),
    loading,
    requestInstance,
  };
}

export default useRequestCallback;
